/**
 * Фильтр списка звонков по критериям (#865): условия по умолчанию, подпись
 * условия и разбор параметра `criterion` из адреса. Формат строки условия —
 * общий с сервером (`@aica/api-types`, `criteria-filter.ts`).
 */

import type { TFunction } from 'i18next';
import {
  recordCriteriaFilterSchema,
  type CriterionFilterOption,
  type RecordCriterionCondition,
} from '@aica/api-types/schemas';
import { NOT_MENTIONED_CODE } from '@aica/core/call-criteria';

const K = 'records.criteriaFilter';

/**
 * Условие, с которого начинается строка после выбора критерия: у флажка —
 * «сработал», у оценки — вся шкала, у списка — первое действующее значение
 * (условию списка нужно хотя бы одно).
 */
export function defaultCriterionCondition(
  option: CriterionFilterOption
): RecordCriterionCondition {
  switch (option.resultType) {
    case 'flag':
      return { criterionId: option.id, kind: 'flag', triggered: true };
    case 'score':
      return { criterionId: option.id, kind: 'score', min: 0, max: 100 };
    case 'choice': {
      const first =
        option.options?.find((value) => !value.archived) ?? option.options?.[0];
      return {
        criterionId: option.id,
        kind: 'choice',
        codes: [first?.code ?? NOT_MENTIONED_CODE],
      };
    }
  }
}

/** Подпись значения списка: «не найдено» — словами, неизвестный код — как есть. */
export function criterionValueLabel(
  code: string,
  option: CriterionFilterOption | undefined,
  t: TFunction
): string {
  if (code === NOT_MENTIONED_CODE) return t(`${K}.notMentioned`);
  return option?.options?.find((value) => value.code === code)?.label ?? code;
}

/**
 * Подходит ли условие критерию из перечня организации: критерий есть, тип
 * условия — его тип, значения списка — его значения или «не найдено». Тот же
 * отбор, что делает сервер; условие, которое не подходит, в запрос не идёт —
 * иначе ссылка на чужой или удалённый критерий навсегда уводила бы список в
 * ошибку 400 (а без флага — 403).
 */
export function conditionFitsOption(
  condition: RecordCriterionCondition,
  option: CriterionFilterOption | undefined
): boolean {
  if (!option || option.resultType !== condition.kind) return false;
  if (condition.kind !== 'choice') return true;
  const known = new Set([
    NOT_MENTIONED_CODE,
    ...(option.options ?? []).map((value) => value.code),
  ]);
  return condition.codes.every((code) => known.has(code));
}

/** Имя критерия в фильтре: архивный помечен — рядом бывает живой с тем же именем. */
export function criterionOptionName(option: CriterionFilterOption, t: TFunction): string {
  return option.archived ? t(`${K}.archivedName`, { name: option.name }) : option.name;
}

/**
 * Текст чипа активного условия: «Имя: значение». При известном перечне условие,
 * которое ему не подходит (критерия нет, другой тип, неизвестное значение), —
 * «Недоступный критерий»: страница его не шлёт, и обычная подпись скрыла бы,
 * что фильтр не применён. Пока перечень неизвестен, условие уходит как есть, и
 * «недоступный» утверждал бы обратное — там имя из последнего прочитанного
 * перечня или просто «Критерий» (#1293).
 */
export function criterionConditionLabel(
  condition: RecordCriterionCondition,
  option: CriterionFilterOption | undefined,
  t: TFunction,
  optionsKnown = true
): string {
  const name =
    optionsKnown && !conditionFitsOption(condition, option)
      ? t(`${K}.unknownCriterion`)
      : option
        ? criterionOptionName(option, t)
        : t(`${K}.criterion`);
  const value =
    condition.kind === 'flag'
      ? t(condition.triggered ? `${K}.triggered` : `${K}.notTriggered`)
      : condition.kind === 'score'
        ? `${condition.min}–${condition.max}`
        : condition.codes.map((code) => criterionValueLabel(code, option, t)).join(', ');
  return `${name}: ${value}`;
}

/**
 * Условия из адреса (`?criterion=…`, одна строка или повторы). Разбирается
 * той же схемой, что и на сервере; неразборчивое целиком отбрасывается — иначе
 * испорченная ссылка давала бы 400 и пустой список вместо обычного.
 */
export function parseCriteriaSearchParam(
  raw: string | readonly string[] | undefined
): RecordCriterionCondition[] {
  if (raw === undefined) return [];
  const parsed = recordCriteriaFilterSchema.safeParse(
    typeof raw === 'string' ? raw : [...raw]
  );
  return parsed.success ? (parsed.data ?? []) : [];
}
